import { useSignal } from "@preact/signals";
import { useEffect, useRef } from "preact/hooks";
import { useAuth } from "../hooks/useAuth";
import {
  globalAuthMode,
  globalAuthReason,
  globalIsGuestReady,
  globalShowAuthModal,
} from "../lib/auth";
import { generateAvatarSeed } from "../lib/utils";

interface Props {
  onSubmit: (
    content: string,
    name: string,
    email: string,
    parentId?: string,
    trap?: string,
  ) => Promise<any>;
  parentId?: string;
  onCancel?: () => void;
  apiUrl: string;
  requireLogin?: boolean;
}

export function CommentForm({ onSubmit, parentId, onCancel, apiUrl, requireLogin }: Props) {
  const content = useSignal("");
  const _password = useSignal("");
  const submitting = useSignal(false);
  const isExpanded = useSignal(!!parentId);
  const trap = useSignal("");
  const resending = useSignal(false);
  const resendSuccess = useSignal(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const {
    authMode,
    authError,
    guestName,
    guestEmail,
    saveInfo,
    widgetUser,
    login,
    register,
    logout,
    handleGuestSubmit,
    resendVerification,
  } = useAuth(apiUrl, !!requireLogin);

  const handleResend = async () => {
    resending.value = true;
    const success = await resendVerification();
    if (success) {
      resendSuccess.value = true;
      setTimeout(() => (resendSuccess.value = false), 5000);
    }
    resending.value = false;
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    authError.value = "";

    const currentUser = widgetUser.peek();

    if (!currentUser && globalIsGuestReady.peek() && !requireLogin) {
      if (!content.value) return;
      submitting.value = true;
      handleGuestSubmit(guestName.value, guestEmail.value);
      await onSubmit(content.value, guestName.value, guestEmail.value, parentId, trap.value);
    } else if (currentUser) {
      if (!content.value) return;
      submitting.value = true;
      await onSubmit(content.value, currentUser.name, currentUser.email, parentId, trap.value);
    }

    content.value = "";
    submitting.value = false;
    isExpanded.value = false;
    if (textareaRef.current) textareaRef.current.style.height = "auto";
    if (onCancel) onCancel();
  };

  const _handleModalSubmit = async (_e: Event) => {
    // This is now handled globally, but we keep it here just in case or remove it.
    // Actually, we can remove handleModalSubmit entirely from CommentForm since it is in AuthModal!
  };

  const handleCancel = () => {
    content.value = "";
    isExpanded.value = false;
    if (textareaRef.current) textareaRef.current.style.height = "auto";
    if (onCancel) onCancel();
  };

  const showCancel = !!onCancel || (!parentId && isExpanded.value);
  const avatarSeedPreview = useSignal("guest");

  useEffect(() => {
    const rawEmail = widgetUser.value ? widgetUser.value.email : guestEmail.value;
    if (widgetUser.value?.avatarSeed) {
      avatarSeedPreview.value = widgetUser.value.avatarSeed;
    } else {
      generateAvatarSeed(rawEmail).then((seed) => (avatarSeedPreview.value = seed));
    }
  }, [guestEmail.value, widgetUser.value]);

  const getAvatarUrl = () =>
    `https://api.dicebear.com/10.x/thumbs/svg?seed=${avatarSeedPreview.value}`;

  const canComment = !!widgetUser.value || (globalIsGuestReady.value && !requireLogin);

  return (
    <div class={`w-full ${parentId ? "mt-4" : ""}`}>
      {canComment || parentId ? (
        <form
          onSubmit={
            !canComment
              ? (e) => {
                  e.preventDefault();
                  globalAuthReason.value = "comment";
                  globalAuthMode.value = "login";
                  globalShowAuthModal.value = true;
                }
              : handleSubmit
          }
          class="w-full"
        >
          {canComment && (
            <input
              type="text"
              name="_diskus_trap"
              value={trap.value}
              onInput={(e) => (trap.value = (e.target as HTMLInputElement).value)}
              style={{ display: "none", position: "absolute", opacity: 0 }}
              tabIndex={-1}
              autoComplete="off"
            />
          )}
          <div
            class={`border border-gray-200 dark:border-gray-700 overflow-hidden focus-within:border-gray-300 dark:focus-within:border-gray-600 transition-all duration-300 ease-in-out bg-white dark:bg-[#181818] rounded-2xl shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] ${!canComment ? "cursor-text" : ""}`}
            onClick={
              !canComment
                ? () => {
                    globalAuthReason.value = "comment";
                    globalAuthMode.value = "login";
                    globalShowAuthModal.value = true;
                  }
                : undefined
            }
          >
            <div
              class={`flex gap-3 px-4 pt-4 pb-3 items-start ${!canComment ? "pointer-events-none" : ""}`}
            >
              <div class="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-800 shrink-0 overflow-hidden flex items-center justify-center border border-gray-200 dark:border-gray-700 mt-0.5">
                <img
                  src={getAvatarUrl()}
                  alt="Avatar"
                  class="w-full h-full object-cover opacity-90 dark:opacity-80"
                />
              </div>
               <textarea
                ref={!canComment ? undefined : textareaRef}
                class="flex-1 py-1 px-0 text-[16px] text-gray-900 dark:text-gray-100 resize-none outline-none placeholder-gray-400 dark:placeholder-gray-500 bg-transparent transition-all duration-300 ease-in-out"
                placeholder={!canComment ? "Write a reply..." : "Write a comment..."}
                value={!canComment ? "" : content.value}
                readOnly={!canComment}
                onFocus={!canComment ? undefined : () => (isExpanded.value = true)}
                onInput={
                  !canComment
                    ? undefined
                    : (e) => {
                        content.value = (e.target as HTMLTextAreaElement).value;
                        (e.target as HTMLTextAreaElement).style.height = "auto";
                        (e.target as HTMLTextAreaElement).style.height =
                          `${(e.target as HTMLTextAreaElement).scrollHeight}px`;
                      }
                }
                required={canComment}
                style={{ minHeight: !canComment || isExpanded.value ? "80px" : "28px" }}
              />
            </div>

            <div
              class={`grid transition-all duration-300 ease-in-out ${isExpanded.value || !canComment ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
            >
              <div class="overflow-hidden">
                <div class="flex flex-col min-[480px]:flex-row min-[480px]:items-center justify-between border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-[#181818]">
                  <div
                    class={`flex items-center justify-between min-[480px]:justify-start gap-3 min-[480px]:gap-4 flex-1 min-w-0 px-4 min-[480px]:px-5 py-3 ${!canComment ? "hidden min-[480px]:flex" : ""}`}
                  >
                    {!canComment ? (
                      <span class="text-[13px] text-gray-500 dark:text-gray-400 font-medium">
                        Sign in to reply
                      </span>
                    ) : (
                      <>
                        <div class="flex items-center gap-2.5 min-w-0 shrink">
                          <span
                            class={`w-2.5 h-2.5 rounded-full shrink-0 ${widgetUser.value ? "bg-[#22c55e]" : "bg-gray-400 dark:bg-gray-500"}`}
                          ></span>
                          <strong class="text-[14px] min-[480px]:text-[15px] text-[#111827] dark:text-gray-100 font-semibold truncate">
                            {widgetUser.value
                              ? widgetUser.value.name
                              : `${guestName.value} (Guest)`}
                          </strong>
                          {widgetUser.value &&
                            widgetUser.value.role === "commenter" &&
                            widgetUser.value.isVerified === false && (
                              <div class="flex items-center gap-2 ml-1">
                                <span class="text-[11px] text-orange-600 bg-orange-50 border border-orange-200 dark:border-orange-900/30 dark:text-orange-400 dark:bg-orange-900/20 px-1.5 py-0.5 rounded-full whitespace-nowrap hidden sm:inline-block">
                                  Unverified
                                </span>
                                <button
                                  type="button"
                                  onClick={handleResend}
                                  disabled={resending.value || resendSuccess.value}
                                  class="text-[12px] text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50 disabled:no-underline whitespace-nowrap"
                                >
                                  {resendSuccess.value
                                    ? "Sent!"
                                    : resending.value
                                      ? "Sending..."
                                      : "Resend Email"}
                                </button>
                              </div>
                            )}
                        </div>
                        <div class="flex items-center gap-3 min-[480px]:gap-4 shrink-0">
                          <div class="hidden min-[480px]:block w-px h-5 bg-gray-200 dark:bg-gray-700 shrink-0"></div>
                          {widgetUser.value ? (
                            <>
                              {widgetUser.value.hasPassword === false && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    globalAuthMode.value = "set_password";
                                    globalShowAuthModal.value = true;
                                  }}
                                  class="text-[12px] font-medium text-blue-600 dark:text-blue-400 hover:underline"
                                  title="Set a password to allow email login"
                                >
                                  Set Password
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={logout}
                                class="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 transition-colors flex items-center justify-center shrink-0"
                                title="Logout"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  stroke-width="2.5"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                >
                                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                                  <polyline points="16 17 21 12 16 7"></polyline>
                                  <line x1="21" y1="12" x2="9" y2="12"></line>
                                </svg>
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                globalIsGuestReady.value = false;
                                globalAuthReason.value = "comment";
                                globalShowAuthModal.value = true;
                                globalAuthMode.value = "guest";
                              }}
                              class="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 transition-colors flex items-center justify-center shrink-0 text-[13px] font-medium"
                              title="Edit Guest Info"
                            >
                              Change
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <div
                    class={`flex items-center justify-end min-[480px]:justify-end gap-3 min-[480px]:gap-4 shrink-0 w-full min-[480px]:w-auto px-4 min-[480px]:px-5 py-3 min-[480px]:py-3 ${!canComment ? "" : "border-t border-gray-100 dark:border-gray-800"} min-[480px]:border-t-0`}
                    onClick={!canComment ? (e) => e.stopPropagation() : undefined}
                  >
                    {showCancel && (
                      <button
                        type="button"
                        onClick={!canComment ? onCancel : handleCancel}
                        class="text-[14px] min-[480px]:text-[15px] font-medium text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors shrink-0 mr-auto min-[480px]:mr-0"
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      type={!canComment ? "button" : "submit"}
                      onClick={
                        !canComment
                          ? () => {
                              globalAuthReason.value = "comment";
                              globalAuthMode.value = "login";
                              globalShowAuthModal.value = true;
                            }
                          : undefined
                      }
                      disabled={submitting.value}
                      class="px-5 py-2.5 ml-1 bg-blue-600 text-white text-[14px] min-[480px]:text-[15px] font-medium rounded-xl hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shrink-0"
                    >
                      {submitting.value ? "Sending..." : parentId ? "Reply" : "Comment"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <div class="border border-gray-200 dark:border-gray-800 rounded-2xl bg-white dark:bg-[#151515] p-4 sm:p-5 shadow-sm">
          <div class="flex items-center justify-between gap-3">
            <div class="flex items-center gap-3 sm:gap-4 min-w-0">
              <div class="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gray-50 dark:bg-[#222] border border-gray-100 dark:border-gray-800 flex items-center justify-center shrink-0 shadow-inner overflow-hidden">
                <img
                  src={getAvatarUrl()}
                  alt="Avatar"
                  class="w-full h-full object-cover opacity-90 dark:opacity-80"
                />
              </div>
              <div class="min-w-0">
                <h4 class="text-[14px] sm:text-[16px] font-semibold text-gray-900 dark:text-gray-100 leading-tight truncate">
                  Join the conversation
                </h4>
                <p class="text-[12px] sm:text-[14px] text-gray-500 dark:text-gray-400 mt-0.5 truncate hidden sm:block">
                  Write a comment and connect with others.
                </p>
                <p class="text-[12px] sm:text-[14px] text-gray-500 dark:text-gray-400 mt-0.5 truncate sm:hidden">
                  Write a comment.
                </p>
              </div>
            </div>
            <div class="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => {
                  globalAuthReason.value = "comment";
                  globalAuthMode.value = "login";
                  globalShowAuthModal.value = true;
                }}
                class="px-4 py-2 sm:px-5 sm:py-2.5 bg-blue-600 text-white text-[13px] sm:text-[14px] font-medium rounded-xl hover:bg-blue-700 transition-all shadow-sm"
              >
                Sign In
              </button>
            </div>
          </div>
          <div class="mt-4 sm:mt-5 pt-3 sm:pt-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-center gap-2 text-[12px] sm:text-[13px] text-gray-500 dark:text-gray-400">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span>
              {requireLogin
                ? "Only signed-in users can comment"
                : "Sign in or use a guest account to comment"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
